import { getWhitelistApiToggle, WHITELIST_API_TOGGLE_KEY } from '@/domain/srp/getWhitelistApiToggle'
import { type RobotsProps } from '@/domain/srp/seo/getSrpMetaData'
import { type BasePageProps } from '@/pages/_app'
import { type MetaCategoryOverrideJson, type SrpMetaLegacyDetailsJson } from '@/types/seo'

/**
 * Creates robots meta tag content based on the results count and canonical URL.
 *
 * @param {number} resultsCount - The number of results to determine the robots meta tag content.
 * @param {string | undefined} canonicalUrl - The canonical URL of the page.
 * @param {number} categoryId - The category ID to check for whitelist toggle.
 * @param {BasePageProps} basePageProps - The base page props containing Firebase configuration.
 * @returns {object | void} An object containing the noindex and nofollow properties.
 *
 * @example
 * createRobotsContent(0); // { nofollow: true, noindex: true }
 * createRobotsContent(5); // { noindex: true, nofollow: false }
 * createRobotsContent(6); // { noindex: false, nofollow: false }
 * createRobotsContent(6, ''); // { noindex: true, nofollow: false }
 */
export const createRobotsContent = (
  resultsCount: number,
  canonicalUrl: string | undefined,
  categoryId: number,
  basePageProps: BasePageProps
): RobotsProps => {
  // This is the case for BRP SRP
  if (resultsCount === 0) {
    return { nofollow: true, noindex: true }
  }

  // This is the case for low results SRP
  if (resultsCount <= 5) {
    return { noindex: true, nofollow: false }
  }

  // If there is no canonical URL, and the whitelist toggle is enabled, add noindex
  const whitelistToggle = getWhitelistApiToggle(basePageProps)
  if (!canonicalUrl && whitelistToggle[WHITELIST_API_TOGGLE_KEY] === 1) {
    return { noindex: true, nofollow: false }
  }

  return { noindex: false, nofollow: false }
}

export const isMetaCategoryOverrideJson = (value: unknown): value is MetaCategoryOverrideJson =>
  !!(value as MetaCategoryOverrideJson)?.en.length &&
  !!(value as MetaCategoryOverrideJson)?.fr.length

export const isSrpLegacyMetaDetailsJson = (value: unknown): value is SrpMetaLegacyDetailsJson =>
  !!(value as SrpMetaLegacyDetailsJson)?.en.length &&
  !!(value as SrpMetaLegacyDetailsJson)?.fr.length
